import type { createStore } from 'jotai'
import { dispatch, durableStateAtom } from '@/events/atoms'
import { blend } from '@/events/builders'
import type { EventTuple } from '@/events/types'
import type { VoxelData, VoxelMap } from '@/voxelTypes'
import { type CanvasRequest, keepCanvasRequest } from './canvasRequest'
import { currentPageAtom } from './pageRegistry'

type Store = ReturnType<typeof createStore>

const same = (a: VoxelData, b: VoxelData) =>
  a.type === b.type && a.color === b.color && a.face === b.face && a.topFace === b.topFace && a.material === b.material

/**
 * The cells that turn `now` into `world`: everything not in `world` removed, every cell of `world`
 * that is missing or different put back. Empty when they already match.
 */
export function freshStartEvents(now: VoxelMap, world: VoxelMap): EventTuple[] {
  const gone: number[] = []
  const back = new Map<number, VoxelData>()
  now.forEach((v, key) => {
    const w = world.get(key)
    if (!w) gone.push(key)
    else if (!same(v, w)) back.set(key, w)
  })
  world.forEach((w, key) => {
    if (!now.get(key)) back.set(key, w)
  })
  const events: EventTuple[] = []
  if (gone.length > 0) events.push(blend(gone, 'subtract'))
  if (back.size > 0) events.push(blend(back, 'place'))
  return events
}

/**
 * Put the world back as the page's generator made it (new paper, a new page): one undoable step,
 * the cells only (the meta, such as mirrors or the camera, stays as it is), in the world's own bounds
 * (a device-shaped page keeps its shape). `page` defaults to the open page. Does nothing when the
 * world is already fresh.
 */
export function freshStart(store: Store, page?: { createDocument: (canvas?: CanvasRequest) => { base: VoxelMap } } | null): void {
  const source = page ?? store.get(currentPageAtom)
  if (!source) return
  const durable = store.get(durableStateAtom)
  const events = freshStartEvents(durable.base, source.createDocument(keepCanvasRequest(durable.meta.bounds)).base)
  if (events.length > 0) dispatch(store, events)
}
